import { create } from "zustand";
import type {
  Pattern,
  FxState,
  TrackMutes,
  DrumMutes,
  MelodyGrid,
  SubdivisionMap,
} from "./instrumentStore";
import { DEFAULT_SUBDIVISION_FLAGS } from "../constants/layout";
import { DEFAULT_DECAY } from "../audio/envelope";
import type { KeyboardTrackId, PitchClass, TrackId } from "../constants/scale";

const ALL_TRACK_IDS: readonly TrackId[] = ["drums", "keys", "bass", "lead"];

export type SegmentBars = 1 | 2 | 4;

export interface Segment {
  id: string;
  name: string;
  createdAt: number;
  bars: SegmentBars;
  bpm: number;
  pattern: Pattern;
  melodyGrids: Record<KeyboardTrackId, MelodyGrid>;
  fx: Record<TrackId, FxState>;
  trackMutes: TrackMutes;
  drumMutes: DrumMutes;
  subdivisions: SubdivisionMap;
  rootPitch: PitchClass;
  blueFlat3: boolean;
  blueFlat5: boolean;
}

export type SegmentSnapshot = Omit<
  Segment,
  "id" | "name" | "createdAt" | "bars"
>;

export interface ArrangementState {
  segments: Record<string, Segment>;
  order: string[];
  selectedSegmentId: string | null;
  namingOpen: boolean;
  arrangementPlayMode: boolean;
  activeSegmentId: string | null;
  expanded: boolean;

  captureSegment(name: string, snapshot: SegmentSnapshot): string;
  replaceSegment(id: string, snapshot: SegmentSnapshot): void;
  renameSegment(id: string, name: string): void;
  removeSegment(id: string): void;
  duplicateSegment(id: string): void;
  moveSegment(id: string, offset: -1 | 1): void;
  reorderSegment(from: number, to: number): void;
  setSegmentBars(id: string, bars: SegmentBars): void;
  selectSegment(id: string | null): void;
  openNaming(): void;
  closeNaming(): void;
  setArrangementPlayMode(on: boolean): void;
  setActiveSegment(id: string | null): void;
  setExpanded(expanded: boolean): void;
  clearArrangement(): void;
}

export const STORAGE_KEY = "phinstrument:arrangement:v1";

interface PersistedShape {
  segments: Record<string, Segment>;
  order: string[];
}

function generateId(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  return `seg-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function clone<T>(value: T): T {
  if (typeof globalThis.structuredClone === "function") {
    return globalThis.structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value));
}

function migrateSubdivisions(raw: unknown): SubdivisionMap {
  const out = {
    drums: { ...DEFAULT_SUBDIVISION_FLAGS },
    keys: { ...DEFAULT_SUBDIVISION_FLAGS },
    bass: { ...DEFAULT_SUBDIVISION_FLAGS },
    lead: { ...DEFAULT_SUBDIVISION_FLAGS },
  } as SubdivisionMap;
  if (!raw || typeof raw !== "object") return out;
  const r = raw as Record<string, unknown>;
  for (const track of ALL_TRACK_IDS) {
    const entry = r[track];
    if (!entry || typeof entry !== "object") continue;
    const e = entry as Record<string, unknown>;
    const base =
      e.base === "quarter" || e.base === "eighth" || e.base === "sixteenth"
        ? e.base
        : "eighth";
    const triplet = e.triplet === true;
    out[track] = { base, triplet };
  }
  return out;
}

function migrateFx(raw: unknown): Record<TrackId, FxState> {
  const source = (raw ?? {}) as Partial<Record<TrackId, Partial<FxState>>>;
  const out = {} as Record<TrackId, FxState>;
  for (const track of ALL_TRACK_IDS) {
    const f = source[track] ?? {};
    out[track] = {
      volume: typeof f.volume === "number" ? f.volume : 0.85,
      fuzz: typeof f.fuzz === "number" ? f.fuzz : 0,
      delay: typeof f.delay === "number" ? f.delay : 0,
      reverb: typeof f.reverb === "number" ? f.reverb : 0,
      decay: typeof f.decay === "number" ? f.decay : DEFAULT_DECAY,
    };
  }
  return out;
}

function migrateSegment(raw: unknown): Segment | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown> & Partial<Segment>;
  if (typeof r.id !== "string" || typeof r.name !== "string") return null;
  return {
    ...(r as Segment),
    subdivisions: migrateSubdivisions(r.subdivisions),
    fx: migrateFx(r.fx),
  };
}

function hydrate(): PersistedShape {
  if (typeof globalThis.localStorage === "undefined") {
    return { segments: {}, order: [] };
  }
  try {
    const raw = globalThis.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { segments: {}, order: [] };
    const parsed = JSON.parse(raw) as {
      segments?: Record<string, unknown>;
      order?: unknown;
    };
    if (!parsed || typeof parsed !== "object")
      return { segments: {}, order: [] };
    const segments: Record<string, Segment> = {};
    for (const [id, value] of Object.entries(parsed.segments ?? {})) {
      const migrated = migrateSegment(value);
      if (migrated) segments[id] = migrated;
    }
    const order = Array.isArray(parsed.order)
      ? (parsed.order as unknown[]).filter(
          (id): id is string => typeof id === "string" && id in segments,
        )
      : [];
    return { segments, order };
  } catch {
    return { segments: {}, order: [] };
  }
}

function persist(state: PersistedShape): void {
  if (typeof globalThis.localStorage === "undefined") return;
  try {
    globalThis.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ segments: state.segments, order: state.order }),
    );
  } catch {
    // Quota or privacy mode — silently ignore; app still works in memory.
  }
}

function clamp(index: number, length: number): number {
  if (index < 0) return 0;
  if (index >= length) return length - 1;
  return index;
}

const initial = hydrate();

export const useArrangementStore = create<ArrangementState>((set, get) => ({
  segments: initial.segments,
  order: initial.order,
  selectedSegmentId: null,
  namingOpen: false,
  arrangementPlayMode: false,
  activeSegmentId: null,
  expanded: false,

  captureSegment: (name, snapshot) => {
    const id = generateId();
    const trimmed = name.trim();
    const fallback = `Segment ${get().order.length + 1}`;
    const segment: Segment = {
      id,
      name: trimmed === "" ? fallback : trimmed,
      createdAt: Date.now(),
      bars: 1,
      ...clone(snapshot),
    };
    set((s) => {
      const next = {
        segments: { ...s.segments, [id]: segment },
        order: [...s.order, id],
      };
      persist(next);
      return { ...next, selectedSegmentId: id };
    });
    return id;
  },

  replaceSegment: (id, snapshot) =>
    set((s) => {
      const segment = s.segments[id];
      if (!segment) return s;
      const updated: Segment = {
        ...segment,
        ...clone(snapshot),
        id: segment.id,
        name: segment.name,
        createdAt: segment.createdAt,
        bars: segment.bars,
      };
      const nextSegments = { ...s.segments, [id]: updated };
      persist({ segments: nextSegments, order: s.order });
      return { segments: nextSegments };
    }),

  renameSegment: (id, name) =>
    set((s) => {
      const segment = s.segments[id];
      if (!segment) return s;
      const trimmed = name.trim();
      if (trimmed === "") return s;
      const nextSegments = {
        ...s.segments,
        [id]: { ...segment, name: trimmed },
      };
      persist({ segments: nextSegments, order: s.order });
      return { segments: nextSegments };
    }),

  removeSegment: (id) =>
    set((s) => {
      if (!s.segments[id]) return s;
      const nextSegments = { ...s.segments };
      delete nextSegments[id];
      const nextOrder = s.order.filter((other) => other !== id);
      persist({ segments: nextSegments, order: nextOrder });
      return {
        segments: nextSegments,
        order: nextOrder,
        selectedSegmentId:
          s.selectedSegmentId === id ? null : s.selectedSegmentId,
        activeSegmentId: s.activeSegmentId === id ? null : s.activeSegmentId,
      };
    }),

  duplicateSegment: (id) =>
    set((s) => {
      const original = s.segments[id];
      if (!original) return s;
      const newId = generateId();
      const copy: Segment = {
        ...clone(original),
        id: newId,
        name: `${original.name} copy`,
        createdAt: Date.now(),
      };
      const insertAt = s.order.indexOf(id);
      const nextOrder = [...s.order];
      nextOrder.splice(insertAt + 1, 0, newId);
      const nextSegments = { ...s.segments, [newId]: copy };
      persist({ segments: nextSegments, order: nextOrder });
      return { segments: nextSegments, order: nextOrder };
    }),

  moveSegment: (id, offset) =>
    set((s) => {
      const currentIndex = s.order.indexOf(id);
      if (currentIndex === -1) return s;
      const target = clamp(currentIndex + offset, s.order.length);
      if (target === currentIndex) return s;
      const nextOrder = [...s.order];
      const [item] = nextOrder.splice(currentIndex, 1);
      nextOrder.splice(target, 0, item);
      persist({ segments: s.segments, order: nextOrder });
      return { order: nextOrder };
    }),

  reorderSegment: (from, to) =>
    set((s) => {
      if (from < 0 || from >= s.order.length) return s;
      const clampedTo = clamp(to, s.order.length);
      if (from === clampedTo) return s;
      const nextOrder = [...s.order];
      const [item] = nextOrder.splice(from, 1);
      nextOrder.splice(clampedTo, 0, item);
      persist({ segments: s.segments, order: nextOrder });
      return { order: nextOrder };
    }),

  setSegmentBars: (id, bars) =>
    set((s) => {
      const segment = s.segments[id];
      if (!segment) return s;
      const nextSegments = { ...s.segments, [id]: { ...segment, bars } };
      persist({ segments: nextSegments, order: s.order });
      return { segments: nextSegments };
    }),

  selectSegment: (id) => set({ selectedSegmentId: id }),
  openNaming: () => set({ namingOpen: true }),
  closeNaming: () => set({ namingOpen: false }),
  setArrangementPlayMode: (on) => set({ arrangementPlayMode: on }),
  setActiveSegment: (id) => set({ activeSegmentId: id }),
  setExpanded: (expanded) => set({ expanded }),

  clearArrangement: () => {
    persist({ segments: {}, order: [] });
    set({
      segments: {},
      order: [],
      selectedSegmentId: null,
      activeSegmentId: null,
      arrangementPlayMode: false,
      expanded: false,
    });
  },
}));

export function __resetArrangementStoreForTests() {
  if (typeof globalThis.localStorage !== "undefined") {
    globalThis.localStorage.removeItem(STORAGE_KEY);
  }
  useArrangementStore.setState({
    segments: {},
    order: [],
    selectedSegmentId: null,
    namingOpen: false,
    arrangementPlayMode: false,
    activeSegmentId: null,
    expanded: false,
  });
}
